var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable react/require-default-props */
import React from 'react';
import ReactDOM from 'react-dom';
import clsx from 'classnames';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Toolbar } from '@ui/molecules/Toolbar';
import { Button } from '@ui/atoms/Button';
import { showErrorToast } from '@ui';
import { translate } from '@utils/i18n';
import apiClient from '@utils/api-client';
import { useAnchoredSelectionToolbar } from '@ui/molecules/Toolbar/useAnchoredSelectionToolbar';
const NS = 'features.proposals.deficiencies_step';
const t = (key) => translate(key, { namespace: NS });
// ---------------------------------------------------------------------------
// Query key constants — exported for task-06 page queries to share the same
// cache namespace and invalidation targets.
// ---------------------------------------------------------------------------
export const proposalDeficiencyKeys = {
    all: (proposalId) => ['proposal-deficiencies', proposalId],
    linked: (proposalId) => ['proposal-deficiencies', proposalId, 'linked'],
    candidates: (proposalId) => ['proposal-deficiencies', proposalId, 'candidates'],
};
// ---------------------------------------------------------------------------
// Action buttons only — no Toolbar wrapper (decision 15 / step 1)
//
// Table view:   these are placed inside Table's toolbar as a group
//               (DeficienciesTable.tsx toolbar.groups).
// Grid view:    wrapped by DeficienciesMassActionsToolbar below.
// ---------------------------------------------------------------------------
/**
 * Bulk-action button group for the Proposal DeficienciesStep listing.
 *
 * Renders action buttons only — no `<Toolbar>` or `<Toolbar.Selection>` wrapper.
 * Selection is page-scoped only. After each successful mutation, selection is
 * cleared and both linked + candidate query caches are invalidated.
 *
 * candidate mode: "Add to Proposal"
 * linked   mode: "Remove from Proposal", "Include Photos", "Exclude Photos"
 */
export function MassActions({ mode, proposalId, selectedItems, onClearSelection, }) {
    const queryClient = useQueryClient();
    const count = selectedItems.length;
    const disabled = count === 0;
    const invalidateAll = () => {
        void queryClient.invalidateQueries({ queryKey: proposalDeficiencyKeys.linked(proposalId) });
        void queryClient.invalidateQueries({ queryKey: proposalDeficiencyKeys.candidates(proposalId) });
    };
    const afterSuccess = () => {
        onClearSelection();
        invalidateAll();
    };
    const addMutation = useMutation({
        mutationFn: () => apiClient.post(`/proposals/${proposalId}/link_deficiencies`, {
            deficiencyIds: selectedItems,
        }),
        onSuccess: afterSuccess,
        onError: () => {
            showErrorToast(t('error_bulk_add'));
            invalidateAll();
        },
    });
    const removeMutation = useMutation({
        mutationFn: () => apiClient.delete(`/proposals/${proposalId}/unlink_deficiencies`, {
            data: { deficiencyIds: selectedItems },
        }),
        onSuccess: afterSuccess,
        onError: () => {
            showErrorToast(t('error_bulk_remove'));
            invalidateAll();
        },
    });
    const includePhotosMutation = useMutation({
        mutationFn: () => __awaiter(this, void 0, void 0, function* () {
            for (const deficiencyId of selectedItems) {
                // eslint-disable-next-line no-await-in-loop
                yield apiClient.put(`/proposals/${proposalId}/include_deficiency_photos`, null, {
                    params: { deficiencyId },
                });
            }
        }),
        onSuccess: afterSuccess,
        onError: () => {
            showErrorToast(t('error_bulk_include_photos'));
            invalidateAll();
        },
    });
    const excludePhotosMutation = useMutation({
        mutationFn: () => __awaiter(this, void 0, void 0, function* () {
            for (const deficiencyId of selectedItems) {
                // eslint-disable-next-line no-await-in-loop
                yield apiClient.put(`/proposals/${proposalId}/exclude_deficiency_photos`, null, {
                    params: { deficiencyId },
                });
            }
        }),
        onSuccess: afterSuccess,
        onError: () => {
            showErrorToast(t('error_bulk_exclude_photos'));
            invalidateAll();
        },
    });
    return (React.createElement(React.Fragment, null,
        mode === 'candidate' && (React.createElement(Button, { type: "button", variant: "default", disabled: disabled || addMutation.isLoading, onClick: () => addMutation.mutate() }, t('bulk_add'))),
        mode === 'linked' && (React.createElement(React.Fragment, null,
            React.createElement(Button, { type: "button", variant: "default", disabled: disabled || removeMutation.isLoading, onClick: () => removeMutation.mutate() }, t('bulk_remove')),
            React.createElement(Button, { type: "button", variant: "default", disabled: disabled || includePhotosMutation.isLoading, onClick: () => includePhotosMutation.mutate() }, t('bulk_include_photos')),
            React.createElement(Button, { type: "button", variant: "default", disabled: disabled || excludePhotosMutation.isLoading, onClick: () => excludePhotosMutation.mutate() }, t('bulk_exclude_photos'))))));
}
/**
 * Thin Toolbar wrapper around MassActions — used only in grid view.
 * When anchorElement is provided and non-null, the bar floats anchored
 * to the first selected card and sticks under the page header (task-13).
 * Hidden entirely when nothing is selected.
 */
export function DeficienciesMassActionsToolbar({ mode, proposalId, selectedItems, totalCount, onSelectAll, onClearSelection, anchorElement, contentWidth = '100%', }) {
    const count = selectedItems.length;
    const isFloating = anchorElement !== undefined;
    // Prefer the fixed global header; fall back to the sticky workspace header.
    const pageHeader = typeof document !== 'undefined'
        ? document.querySelector('#global-header, .global-header, nav.navbar, .qmb-ui-header--sticky, .qmb-ui-header')
        : null;
    const { stuck, style } = useAnchoredSelectionToolbar(isFloating ? anchorElement !== null && anchorElement !== void 0 ? anchorElement : null : null, pageHeader, contentWidth);
    if (count === 0) {
        return null;
    }
    const toolbarEl = (React.createElement(Toolbar, null,
        React.createElement(Toolbar.Selection, { count: count, totalCount: totalCount, selectAll: false, onSelectAll: () => onSelectAll(true), selectAllLabel: t('select_all_on_page'), selectedLabel: count > 0 ? translate('selected_count', { namespace: NS, count }) : undefined, selectPageLabel: t('select_all_on_page'), onSelectPage: () => onSelectAll(true), clearLabel: t('clear_selection'), onClear: onClearSelection }),
        React.createElement(Toolbar.Group, null,
            React.createElement(MassActions, { mode: mode, proposalId: proposalId, selectedItems: selectedItems, onClearSelection: onClearSelection }))));
    if (!isFloating || !anchorElement) {
        return toolbarEl;
    }
    const floatingBar = (React.createElement("div", { className: clsx('qmb-ui-table__floating-toolbar', {
            'qmb-ui-table__floating-toolbar--anchored': !stuck,
            'qmb-ui-table__floating-toolbar--stuck': stuck
        }), style: style }, toolbarEl));
    return ReactDOM.createPortal(floatingBar, document.body);
}
